/**
 * /department — the signed-in user's department (docs/guardian/03 §1.5,
 * 2026-10-21): name, plan, seats and this month's prompts; admins also manage
 * members and invites (DepartmentAdmin). Everything is scoped by the Backend
 * to the caller's own membership; this view sends no department id.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import { fetchGuardianDepartment, leaveDepartment } from '../api/guardianApi';
import DepartmentAdmin from './DepartmentAdmin';
import DepartmentDocuments from './DepartmentDocuments';
import DepartmentRules from './DepartmentRules';
import DepartmentSettings from './DepartmentSettings';
import DepartmentSetup from './DepartmentSetup';
import DepartmentSuggestions from './DepartmentSuggestions';
import DepartmentVocabulary from './DepartmentVocabulary';
import DepartmentStructure from './DepartmentStructure';
import DepartmentDashboard from './DepartmentDashboard';
import DepartmentAreas from './DepartmentAreas';
import DepartmentAlerts from './DepartmentAlerts';
import DepartmentBoard from './DepartmentBoard';
import { departmentErrorMessage } from '../utils/departmentErrors';

function planLabel(plan) {
  if (!plan) return 'No active plan';
  const name = plan.slug ? plan.slug.charAt(0).toUpperCase() + plan.slug.slice(1) : 'Plan';
  return plan.source === 'contract' ? `${name} (contract)` : name;
}

/**
 * @param {object} props
 * @param {string|null} props.authToken
 * @param {string|null} [props.currentUserId] - The caller's Guardian user id (board "mine").
 * @param {() => void} props.onChanged - Re-reads /me after leaving.
 * @param {() => void} props.onBack
 */
export default function DepartmentView({ authToken, currentUserId = null, onChanged, onBack }) {
  const [state, setState] = useState({ status: 'idle', data: null, error: null });
  const [leaveError, setLeaveError] = useState(null);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    if (!authToken) return;
    setState((prev) => ({ ...prev, status: 'loading', error: null }));
    const res = await fetchGuardianDepartment(authToken);
    if (mine !== seq.current) return;
    if (res?.success) {
      setState({ status: 'ready', data: res.data, error: null });
    } else if (res?.data?.code === 'not_a_member') {
      setState({ status: 'none', data: null, error: null });
    } else {
      setState({ status: 'error', data: null, error: departmentErrorMessage(res, 'Could not load your department.') });
    }
  }, [authToken]);

  useEffect(() => {
    load();
    return () => {
      seq.current += 1;
    };
  }, [load]);

  const onLeave = async () => {
    setLeaveError(null);
    const res = await leaveDepartment(authToken);
    if (res?.success) {
      setConfirmLeave(false);
      onChanged?.();
      setState({ status: 'none', data: null, error: null });
      return;
    }
    setLeaveError(departmentErrorMessage(res, 'Could not leave the department.'));
  };

  const { status, data, error } = state;
  const dept = data?.department;
  const isAdmin = dept?.role === 'admin';

  return (
    <Box sx={{ maxWidth: 880, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h5" component="h1">{dept?.name || 'Department'}</Typography>
        <Button size="small" onClick={onBack}>Back to chat</Button>
      </Box>

      {status === 'loading' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} role="status">
          <CircularProgress size={20} />
          <Typography variant="body2">Loading your department…</Typography>
        </Box>
      )}

      {status === 'error' && (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {status === 'none' && (
        <Alert severity="info">
          You are not part of a department. To join one, open the invitation link your department
          admin sent you.
        </Alert>
      )}

      {status === 'ready' && data && (
        <>
          <Paper variant="outlined" sx={{ p: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Plan</Typography>
              <Typography variant="body1">{planLabel(data.plan)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Seats in use</Typography>
              <Typography variant="body1">
                {data.seats?.used ?? 0} of {data.seats?.limit ?? 0}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Questions this month</Typography>
              <Typography variant="body1">
                {data.usage?.monthPrompts ?? 0}
                {data.plan?.monthlyLimit ? ` of ${data.plan.monthlyLimit}` : ''}
              </Typography>
            </Box>
          </Paper>
          <Typography variant="body2" color="text.secondary">
            Your role: {isAdmin ? 'Admin' : 'Member'}
          </Typography>
          {/* Learning-loop member notice (D4, docs/guardian/10, 2026-10-24).
              Shown to every member only when the signals flag is on. */}
          {data.learningLoop === true && (
            <Typography variant="body2" color="text.secondary">
              {"Questions you ask may be shown to your department's admins, without your name, to keep your department's knowledge current."}
            </Typography>
          )}

          {/* Organization features (WS-F, docs/guardian/06, 2026-10-23): shared
              dashboard, watched areas, team alerts and the department board.
              Each hides itself when the feature is switched off (503). */}
          <DepartmentDashboard authToken={authToken} />
          <DepartmentAreas authToken={authToken} isAdmin={isAdmin} />
          <DepartmentAlerts authToken={authToken} currentUserId={currentUserId} />
          <DepartmentBoard authToken={authToken} isAdmin={isAdmin} currentUserId={currentUserId} />

          {/* Department setup in one sitting (D3, docs/guardian/09, 2026-10-23). */}
          {isAdmin && <DepartmentSetup authToken={authToken} />}
          {/* Learning-loop suggestions from member signals (D4, docs/guardian/10, 2026-10-24). */}
          {isAdmin && <DepartmentSuggestions authToken={authToken} />}
          {isAdmin && <DepartmentAdmin authToken={authToken} onChanged={load} />}
          {/* SOPs and other department documents (D1, 2026-10-22). */}
          {isAdmin && <DepartmentDocuments authToken={authToken} />}
          {isAdmin && <DepartmentRules authToken={authToken} />}
          {/* Terms, structure and language settings (D2, 2026-10-22). Members
              see the approved vocabulary/structure read-only; admins manage them. */}
          {isAdmin && <DepartmentSettings authToken={authToken} />}
          <DepartmentVocabulary authToken={authToken} isAdmin={isAdmin} />
          <DepartmentStructure authToken={authToken} isAdmin={isAdmin} />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
            {!confirmLeave ? (
              <Box>
                <Button color="error" variant="text" onClick={() => setConfirmLeave(true)}>
                  Leave department
                </Button>
              </Box>
            ) : (
              <Alert
                severity="warning"
                action={(
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button color="inherit" size="small" onClick={() => setConfirmLeave(false)}>Cancel</Button>
                    <Button color="error" size="small" onClick={onLeave}>Leave</Button>
                  </Box>
                )}
              >
                You will lose your department&apos;s plan and go back to your personal plan.
              </Alert>
            )}
            {leaveError && <Alert severity="error">{leaveError}</Alert>}
          </Box>
        </>
      )}
    </Box>
  );
}
